import { StanButton, StanCalendar, StanDropdown, StanInput, StanModal, StanTextArea } from '@/components';
import { StanFloatingMenu } from '@/components/core-components/StanDropdown/FloatingMenu/StanFloatingMenu';
import { FiChevronDown } from 'react-icons/fi';
import { useRescheduleModal } from '../../hooks/useRescheduleModal';

interface RescheduleModalProps {
    isOpen: boolean;
    onClose: () => void;
    bookingTitle: string;
    attendeeName: string;
    bookingId: string;
    currentStartDateTime: string;
    onSave: (rescheduleData: { date: string; startTime: string; endTime: string; reason?: string }) => Promise<void>;
    isLoading?: boolean;
}

export function RescheduleModal({
    isOpen,
    onClose,
    bookingTitle,
    attendeeName,
    bookingId,
    currentStartDateTime,
    onSave,
    isLoading = false,
}: RescheduleModalProps) {
    const {
        selectedDate,
        calendarMonth,
        reason,
        currentStartTime: startTime,
        currentEndTime: endTime,
        filteredStartTimeOptions,
        filteredEndTimeOptions,
        disabledDates,
        isValid,
        formattedDate,
        setCalendarMonth,
        setReason,
        handleStartTimeSelect,
        handleEndTimeSelect,
        handleDateSelect,
        handleSave,
    } = useRescheduleModal({
        bookingId,
        isOpen,
        initialStartDateTime: currentStartDateTime,
    });

    return (
        <StanModal id='reschedule-modal' isOpen={isOpen} onClose={onClose} title='Reschedule Your Meeting' size='md'>
            <div className='space-y-6'>
                <p className='text-gray text-body-sm text-center'>
                    &quot;{bookingTitle}&quot; with {attendeeName}
                </p>

                <div className='space-y-2'>
                    <label className='text-dark text-body-sm block font-medium'>Date</label>
                    <StanFloatingMenu
                        id='date-picker'
                        trigger={
                            <div className='relative'>
                                <StanInput
                                    id='date-picker'
                                    name='date-picker'
                                    value={formattedDate}
                                    onChange={() => {}}
                                    readOnly
                                    placeholder='Select Date'
                                    className='cursor-pointer'
                                />
                                <FiChevronDown className='text-gray pointer-events-none absolute top-1/2 right-3 size-5 -translate-y-1/2' />
                            </div>
                        }
                    >
                        {({ closeMenu }) => (
                            <div className='w-auto'>
                                <StanCalendar
                                    analyticsName='reschedule-date-picker'
                                    mode='single'
                                    selected={selectedDate}
                                    defaultMonth={selectedDate}
                                    month={calendarMonth}
                                    onMonthChange={setCalendarMonth}
                                    onSelect={date => handleDateSelect(date, closeMenu)}
                                    disabled={disabledDates}
                                />
                            </div>
                        )}
                    </StanFloatingMenu>
                </div>

                <div className='space-y-2'>
                    <div className='flex flex-col gap-3 sm:flex-row sm:gap-2'>
                        <div className='w-full sm:flex-1'>
                            <label htmlFor='reschedule-start-time' className='text-label-xs-bold mb-1 block'>
                                From
                            </label>
                            <StanDropdown
                                id='reschedule-start-time'
                                name='reschedule-start-time'
                                options={filteredStartTimeOptions}
                                currentValue={startTime}
                                onSelect={handleStartTimeSelect}
                                preventDeselect
                                disabled={!selectedDate}
                            />
                        </div>
                        <div className='w-full sm:flex-1'>
                            <label htmlFor='reschedule-end-time' className='text-label-xs-bold mb-1 block'>
                                To
                            </label>
                            <StanDropdown
                                id='reschedule-end-time'
                                name='reschedule-end-time'
                                options={filteredEndTimeOptions}
                                currentValue={endTime}
                                onSelect={handleEndTimeSelect}
                                preventDeselect
                                disabled={!selectedDate || !startTime}
                            />
                        </div>
                    </div>
                </div>

                <StanTextArea
                    id='reason'
                    name='reason'
                    label='Reason (optional)'
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder='Reason...'
                    rows={3}
                />

                <div className='flex gap-3 pt-4'>
                    <StanButton
                        name='cancel-reschedule'
                        onClick={onClose}
                        color='secondary'
                        className='flex-1'
                        disabled={isLoading}
                        type='button'
                    >
                        Never Mind
                    </StanButton>
                    <StanButton
                        name='save-reschedule'
                        onClick={() => handleSave(onSave)}
                        color='primary'
                        className='flex-1'
                        disabled={!isValid || isLoading}
                        loading={isLoading}
                        type='button'
                    >
                        Reschedule
                    </StanButton>
                </div>
            </div>
        </StanModal>
    );
}
